import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Divider,
  Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

const StockPurchase = ({ user, onProductsUpdate }) => {
  const [dealers, setDealers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(0);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [purchases, setPurchases] = useState([]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  
  // New product variant dialog
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [newVariant, setNewVariant] = useState({
    name: '',
    itemNumber: '',
    price: 0,
    costPrice: 0,
    brand: '',
    category: '',
    description: ''
  });
  const [pendingItem, setPendingItem] = useState(null);

  useEffect(() => {
    fetchDealers();
    fetchProducts();
    fetchPurchases();
  }, []);

  const fetchDealers = async () => {
    try {
      const res = await fetch('/api/dealers', {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDealers(data);
      }
    } catch (err) {
      console.error('Failed to fetch dealers:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch('/api/products', {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchPurchases = async () => {
    try {
      const res = await fetch('/api/stock-purchases', {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPurchases(data);
      }
    } catch (err) {
      console.error('Failed to fetch purchases:', err);
    }
  };

  const handleAddItem = () => {
    if (!selectedProduct) {
      setMessage({ text: 'Please select a product', type: 'error' });
      return;
    }
    if (quantity <= 0) {
      setMessage({ text: 'Quantity must be greater than 0', type: 'error' });
      return;
    }
    if (unitPrice < 0) {
      setMessage({ text: 'Unit price cannot be negative', type: 'error' });
      return;
    }

    // Check if unit price differs from existing cost price
    const existingCostPrice = selectedProduct.costPrice || 0;
    
    // If price differs from existing cost price (and product has a cost price), suggest creating variant
    if (existingCostPrice > 0 && unitPrice !== existingCostPrice) {
      // Store pending item and open dialog
      setPendingItem({ product: selectedProduct, quantity, unitPrice });
      setNewVariant({
        name: `${selectedProduct.name} (Variant)`,
        itemNumber: `${selectedProduct.itemNumber}-V${Date.now().toString().slice(-4)}`,
        price: selectedProduct.price || unitPrice * 1.3, // Suggest 30% markup
        costPrice: unitPrice,
        brand: selectedProduct.brand || '',
        category: selectedProduct.category || '',
        description: `Variant of ${selectedProduct.name} with different cost price`,
        minStock: selectedProduct.minStock || 0
      });
      setVariantDialogOpen(true);
      return;
    }

    // Add item normally if no existing cost price or prices match
    addItemToList(selectedProduct, quantity, unitPrice);
  };

  const addItemToList = (product, qty, price) => {
    const existingIndex = purchaseItems.findIndex(
      item => item.product._id === product._id && item.unitPrice === price
    );
    
    if (existingIndex >= 0) {
      // Update existing item only if product AND unit price match
      const updated = [...purchaseItems];
      updated[existingIndex] = {
        ...updated[existingIndex],
        quantity: updated[existingIndex].quantity + qty,
        total: (updated[existingIndex].quantity + qty) * price
      };
      setPurchaseItems(updated);
    } else {
      // Add as new item if product is different OR unit price is different
      setPurchaseItems([
        ...purchaseItems,
        {
          product: product,
          quantity: qty,
          unitPrice: price,
          total: qty * price
        }
      ]);
    }

    // Reset
    setSelectedProduct(null);
    setQuantity(1);
    setUnitPrice(0);
    setMessage({ text: '', type: '' });
  };

  const handleCreateVariant = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({
          ...newVariant,
          stock: 0 // Start with 0, will be updated by purchase
        })
      });

      if (res.ok) {
        const createdProduct = await res.json();
        setMessage({ text: 'Product variant created successfully!', type: 'success' });
        
        // Add the new variant to purchase items
        addItemToList(createdProduct, pendingItem.quantity, pendingItem.unitPrice);
        
        // Refresh products list
        if (onProductsUpdate) onProductsUpdate();
        
        // Close dialog and reset
        setVariantDialogOpen(false);
        setPendingItem(null);
        setNewVariant({
          name: '',
          itemNumber: '',
          price: 0,
          costPrice: 0,
          brand: '',
          category: '',
          description: ''
        });
      } else {
        const error = await res.json();
        setMessage({ text: error.error || 'Failed to create variant', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Network error. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelVariant = () => {
    setVariantDialogOpen(false);
    setPendingItem(null);
    setMessage({ text: '', type: '' });
  };

  const handleUseExistingProduct = () => {
    // Add item with existing product and update cost price
    if (pendingItem) {
      addItemToList(pendingItem.product, pendingItem.quantity, pendingItem.unitPrice);
      setVariantDialogOpen(false);
      setPendingItem(null);
      setMessage({ text: 'Item added. Product cost price will be updated.', type: 'info' });
    }
  };

  const handleRemoveItem = (index) => {
    setPurchaseItems(purchaseItems.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return purchaseItems.reduce((sum, item) => sum + item.total, 0);
  };

  const handleSavePurchase = async () => {
    if (!selectedDealer) {
      setMessage({ text: 'Please select a dealer', type: 'error' });
      return;
    }
    if (purchaseItems.length === 0) {
      setMessage({ text: 'Please add at least one item', type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const res = await fetch('/api/stock-purchases', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({
          dealer: selectedDealer._id,
          items: purchaseItems.map(item => ({
            product: item.product._id,
            quantity: item.quantity,
            unitPrice: item.unitPrice
          })),
          invoiceNumber,
          purchaseDate,
          notes,
          total: calculateTotal()
        })
      });

      if (res.ok) {
        setMessage({ text: 'Stock purchase recorded successfully! Stock updated.', type: 'success' });
        // Reset form
        setSelectedDealer(null);
        setPurchaseItems([]);
        setInvoiceNumber('');
        setNotes('');
        setPurchaseDate(new Date().toISOString().split('T')[0]);
        // Refresh lists
        if (onProductsUpdate) onProductsUpdate();
        fetchPurchases();
      } else {
        const error = await res.json();
        setMessage({ text: error.error || 'Failed to save purchase', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Network error. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', bgcolor: '#f0f2f5', display: 'flex', overflow: 'hidden' }}>
      {/* Left Side - Purchase Form */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '2px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalShippingIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Stock Purchase / Arrivals
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Record dealer invoices and update inventory stock
            </Typography>
          </Box>
        </Box>

        {message.text && (
          <Alert severity={message.type} sx={{ m: 2, mb: 0 }} onClose={() => setMessage({ text: '', type: '' })}>
            {message.text}
          </Alert>
        )}

        {/* Form Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <Stack spacing={2}>
            {/* Dealer & Invoice Info */}
            <Paper sx={{ p: 2, bgcolor: 'white' }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.secondary">
                PURCHASE DETAILS
              </Typography>
              <Stack spacing={1.5}>
                <Autocomplete
                  value={selectedDealer}
                  onChange={(e, newValue) => setSelectedDealer(newValue)}
                  options={dealers}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => <TextField {...params} label="Dealer" placeholder="Select dealer" />}
                  size="small"
                />
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Invoice Number"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="INV-001"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Date"
                    type="date"
                    value={purchaseDate}
                    onChange={(e) => setPurchaseDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Stack>
              </Stack>
            </Paper>

            {/* Add Items Section */}
            <Paper sx={{ p: 2, bgcolor: 'white' }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.secondary">
                ADD ITEMS
              </Typography>
              <Stack spacing={1.5}>
                <Autocomplete
                  value={selectedProduct}
                  onChange={(e, newValue) => {
                    setSelectedProduct(newValue);
                    if (newValue) {
                      setUnitPrice(newValue.costPrice || newValue.price || 0);
                    }
                  }}
                  options={products}
                  getOptionLabel={(option) => `${option.name} (${option.itemNumber || 'N/A'})`}
                  renderInput={(params) => <TextField {...params} label="Product" placeholder="Search product" />}
                  size="small"
                />
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Quantity"
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                    inputProps={{ min: 1 }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Unit Price"
                    type="number"
                    value={unitPrice}
                    onChange={(e) => setUnitPrice(Number(e.target.value))}
                    inputProps={{ min: 0, step: 0.01 }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                    disabled={!selectedProduct}
                    sx={{ minWidth: 100 }}
                  >
                    Add
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {/* Items List */}
            {purchaseItems.length > 0 && (
              <Paper sx={{ p: 2, bgcolor: 'white' }}>
                <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.secondary">
                  ITEMS ({purchaseItems.length})
                </Typography>
                <Stack spacing={1}>
                  {purchaseItems.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        bgcolor: '#fafafa',
                        borderRadius: 1,
                        border: '1px solid #e8e8e8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {item.product.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.quantity} × {formatCurrency(item.unitPrice)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={700} color="primary" sx={{ mr: 2 }}>
                        {formatCurrency(item.total)}
                      </Typography>
                      <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
                    <Typography variant="h6" fontWeight={700}>Grand Total:</Typography>
                    <Typography variant="h6" fontWeight={700} color="primary.main">
                      {formatCurrency(calculateTotal())}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Notes */}
            <Paper sx={{ p: 2, bgcolor: 'white' }}>
              <TextField
                label="Notes (Optional)"
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes about this purchase..."
                size="small"
                fullWidth
              />
            </Paper>

            {/* Save Button */}
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={handleSavePurchase}
              disabled={loading || !selectedDealer || purchaseItems.length === 0}
              startIcon={<InventoryIcon />}
            >
              {loading ? 'Saving...' : 'Record Purchase & Update Stock'}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Right Side - Purchase History */}
      <Paper 
        elevation={3}
        sx={{ 
          width: { xs: '100%', md: 450 }, 
          display: 'flex', 
          flexDirection: 'column',
          borderRadius: 0,
          borderLeft: '2px solid #e0e0e0'
        }}
      >
        <Box sx={{ 
          p: 2, 
          bgcolor: '#1976d2', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <InventoryIcon />
          <Typography variant="h6" fontWeight={700}>
            Recent Purchases
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#fafafa' }}>
          {purchases.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
              <InventoryIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" fontWeight={500}>
                No purchases recorded yet
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1} sx={{ p: 2 }}>
              {purchases.slice(0, 10).map((purchase) => (
                <Paper
                  key={purchase.id || purchase._id}
                  sx={{
                    p: 1.5,
                    border: '1px solid #e8e8e8',
                    '&:hover': {
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      borderColor: '#1976d2'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {purchase.dealer?.name || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(purchase.purchaseDate || purchase.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} color="primary">
                      {formatCurrency(purchase.total)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip label={purchase.invoiceNumber || 'N/A'} size="small" />
                      <Chip label={`${purchase.items?.length || 0} items`} size="small" variant="outlined" />
                    </Box>
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedPurchase(purchase);
                        setViewDialogOpen(true);
                      }}
                    >
                      View
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Paper>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedPurchase && (
          <>
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h6" fontWeight={700}>Purchase Details</Typography>
                <Chip label={selectedPurchase.invoiceNumber || 'N/A'} color="primary" />
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={3}>
                {/* Basic Info */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Dealer</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {selectedPurchase.dealer?.name || 'Unknown'}
                  </Typography>
                </Box>

                <Divider />

                {/* Purchase Info */}
                <Stack direction="row" spacing={4}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Purchase Date</Typography>
                    <Typography variant="body1">
                      {new Date(selectedPurchase.purchaseDate || selectedPurchase.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Invoice Number</Typography>
                    <Typography variant="body1">{selectedPurchase.invoiceNumber || 'N/A'}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Purchase Code</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {(selectedPurchase.purchaseCode || selectedPurchase._id || '').substring(0, 12)}
                    </Typography>
                  </Box>
                </Stack>

                <Divider />

                {/* Items */}
                <Typography variant="subtitle1" fontWeight={600}>Items Purchased</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="center">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedPurchase.items?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.product?.name || 'Unknown'}</TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.quantity * item.unitPrice)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Total */}
                <Stack direction="row" spacing={4}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Total Items</Typography>
                    <Typography variant="h6">{selectedPurchase.items?.length || 0}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Total Amount</Typography>
                    <Typography variant="h6" fontWeight={600}>{formatCurrency(selectedPurchase.total)}</Typography>
                  </Box>
                </Stack>

                {/* Notes */}
                {selectedPurchase.notes && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                      <Typography variant="body1">{selectedPurchase.notes}</Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create Product Variant Dialog */}
      <Dialog open={variantDialogOpen} onClose={handleCancelVariant} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <InventoryIcon color="warning" />
            <Typography variant="h6">Price Difference Detected</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              The unit price <strong>{formatCurrency(pendingItem?.unitPrice || 0)}</strong> differs from the existing cost price 
              <strong> {formatCurrency(pendingItem?.product?.costPrice || 0)}</strong>.
              <br /><br />
              Choose an option:
              <br />• <strong>Create Variant</strong> - Create a new product with this price (recommended)
              <br />• <strong>Update Existing</strong> - Use existing product and update its cost price
            </Alert>
            
            <Divider />
            
            <Typography variant="subtitle2" fontWeight={600}>New Variant Details:</Typography>
            
            <TextField
              label="Product Name"
              value={newVariant.name}
              onChange={(e) => setNewVariant({ ...newVariant, name: e.target.value })}
              fullWidth
              required
              size="small"
            />
            
            <TextField
              label="Item Number / SKU"
              value={newVariant.itemNumber}
              onChange={(e) => setNewVariant({ ...newVariant, itemNumber: e.target.value })}
              fullWidth
              required
              size="small"
            />
            
            <Stack direction="row" spacing={2}>
              <TextField
                label="Cost Price"
                type="number"
                value={newVariant.costPrice}
                onChange={(e) => setNewVariant({ ...newVariant, costPrice: Number(e.target.value) })}
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
                required
                size="small"
              />
              <TextField
                label="Selling Price"
                type="number"
                value={newVariant.price}
                onChange={(e) => setNewVariant({ ...newVariant, price: Number(e.target.value) })}
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
                required
                size="small"
              />
            </Stack>
            
            <Stack direction="row" spacing={2}>
              <TextField
                label="Brand"
                value={newVariant.brand}
                onChange={(e) => setNewVariant({ ...newVariant, brand: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="Category"
                value={newVariant.category}
                onChange={(e) => setNewVariant({ ...newVariant, category: e.target.value })}
                fullWidth
                size="small"
              />
            </Stack>
            
            <TextField
              label="Description"
              value={newVariant.description}
              onChange={(e) => setNewVariant({ ...newVariant, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelVariant} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUseExistingProduct} 
            variant="outlined"
            disabled={loading}
          >
            Use Existing & Update Price
          </Button>
          <Button 
            onClick={handleCreateVariant} 
            variant="contained" 
            disabled={loading || !newVariant.name || !newVariant.itemNumber}
          >
            {loading ? 'Creating...' : 'Create Variant & Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockPurchase;
